import { cn } from '@/lib/utils';
import { Textarea } from '@/components/ui/textarea';
import { useMemo, useCallback, useRef, forwardRef } from 'react';

// Helper function to escape special characters for use in a RegExp
function escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

interface HighlightableTextareaProps {
    validSentences: string[];
    value?: string;
    onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
    className?: string;
    onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
    placeholder?: string;
}

export const HighlightableTextarea = forwardRef<HTMLTextAreaElement, HighlightableTextareaProps>(({
    validSentences,
    value = '',
    onChange,    // Added onChange prop
    className = '',
    placeholder = 'Start typing here...',
    ...props
}, ref) => {
    const highlighterRef = useRef(null);

    // This internal ref is now only for scroll syncing
    const textareaRef = useRef(null); 

    // Helper to set both the forwarded ref and the internal ref
    const handleRef = useCallback((node) => {
        textareaRef.current = node;
        if (typeof ref === 'function') {
            ref(node);
        } else if (ref) {
            ref.current = node;
        }
    }, [ref]);

    const handleScroll = useCallback(() => {
        if (highlighterRef.current && textareaRef.current) {
            highlighterRef.current.scrollTop = textareaRef.current.scrollTop;
            highlighterRef.current.scrollLeft = textareaRef.current.scrollLeft;
        }
    }, []);

    const highlightedText = useMemo(() => {
        // Use `value` prop instead of internal state
        if (!value) return null;

        // 1. Find all start/end indices for every valid sentence
        const matches = [];
        validSentences.forEach(sentence => {
            if (!sentence) return; // Ignore empty/null sentences
            // Use `value` prop here
            const regex = new RegExp(escapeRegExp(sentence), 'g');
            let match;
            while ((match = regex.exec(value)) !== null) {
                matches.push({ start: match.index, end: match.index + sentence.length });
            }
        });

        if (matches.length === 0) {
            // Use `value` prop here
            return <span className="bg-purple-100 dark:bg-red-900/40 rounded-sm">{value}</span>;
        }

        // 2. Merge overlapping or adjacent "valid" ranges
        matches.sort((a, b) => a.start - b.start);

        const mergedMatches = [matches[0]];
        for (let i = 1; i < matches.length; i++) {
            const current = matches[i];
            const last = mergedMatches[mergedMatches.length - 1];
            if (current.start <= last.end) {
                last.end = Math.max(last.end, current.end);
            } else {
                mergedMatches.push(current);
            }
        }

        // 3. Build the final array of segments (valid and deviation)
        const segments = [];
        let lastIndex = 0;

        mergedMatches.forEach(({ start, end }) => {
            if (start > lastIndex) {
                segments.push({
                    type: 'deviation',
                    content: value.substring(lastIndex, start),
                });
            }
            segments.push({
                type: 'valid',
                content: value.substring(start, end),
            });
            lastIndex = end;
        });

        if (lastIndex < value.length) {
            segments.push({
                type: 'deviation',
                content: value.substring(lastIndex, value.length),
            });
        }

        // 4. Render the segments to JSX
        return segments.map((segment, index) => {
            const style =
                segment.type === 'valid'
                    ? 'bg-transparent dark:bg-transparent rounded-sm'
                    : 'bg-purple-200 dark:bg-purple-900/40 rounded-sm';
            return (
                <span key={index} className={style}>
                    {segment.content}
                </span>
            );
        });
    }, [value, validSentences]); // Depend on `value` prop

    return (
        <div className={cn('relative w-full grid', className)}>
            {/* Highlighter DIV */}
            <div
                ref={highlighterRef}
                className={cn(
                    'z-0 row-start-1 col-start-1 overflow-auto whitespace-pre-wrap break-words pointer-events-none font-mono',
                    'p-2 text-sm border border-transparent'
                )}
                aria-hidden="true"
            >
                {highlightedText}
                <br />
            </div>

            {/* Actual Textarea */}
            <Textarea
                ref={handleRef} // Use the combined ref handler
                value={value}
                onChange={onChange} // Use the passed onChange handler
                onScroll={handleScroll}
                className="z-10 row-start-1 col-start-1 font-mono bg-transparent text-transparent caret-primary resize-none"
                placeholder={placeholder}
                {...props} // Pass down other props like onKeyDown
            />
        </div>
    );
});

HighlightableTextarea.displayName = 'HighlightableTextarea';